import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import i18next from 'i18next';
import _ from 'lodash';
import moment from 'moment';
import { sqHomeScreenStore, sqLicenseManagementStore, sqWorkbenchStore, sqWorksheetStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { formatTime } from '@/datetime/dateTime.utilities';
import { getPreferredTimeFormat } from '@/datetime/dateTimeFormatting.utilities';
import { ITEM_TYPES, tabFolders } from '@/homescreen/homescreen.constants';
import { Feature } from '@/licenseManagement/licenseManagement.constants';
import { hasValidFeatureInLicense } from '@/licenseManagement/licenseManagement.utilities';
import { HOME_SCREEN_TABS, SEARCH_ITEM_LOCATIONS } from '@/main/app.constants';
import { canModifyWorkbook, isAdmin } from '@/services/authorization.service';
import {
  isEveryoneGroupEnabled,
  isExternalOrganizationTabEnabled,
  isVantageNameBasedAssetContextEnabled,
} from '@/services/systemConfiguration.utilities';
import { doTrack } from '@/track/trackProxy.service';
import { addAssetsProperty } from '@/utilities/httpHelpers.utilities';
import { errorToast, successToast, warnToast } from '@/utilities/toast.utilities';
import { equalsIgnoreCase } from '@/utilities/utilities';
import * as setup from '@/vantage/setup';
import { setWorkbookDisplayMode } from '@/workbook/workbook.actions';
import { DEFAULT_WORKBOOK_STATE, WORKBOOK_DISPLAY } from '@/workbook/workbook.constants';
import { setWorkBook } from '@/workbook/workbook.utilities';
import { getWorksheets } from '@/worksheets/worksheets.utilities';
import { FilterEnum, sqFoldersApi } from '@seeqdev/api/endpoint/FoldersApi';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqProjectsApi } from '@seeqdev/api/endpoint/ProjectsApi';
import { sqTableDefinitionsApi } from '@seeqdev/api/endpoint/TableDefinitionsApi';
import { sqUsersApi } from '@seeqdev/api/endpoint/UsersApi';
import { sqWorkbooksApi } from '@seeqdev/api/endpoint/WorkbooksApi';
import type { Folder } from '@/explorer/exploreModal.types';
import type { Item } from '@/tools/histogram/aggregationBin.store';
import type { AnyProperty } from '@/utilities.types';
import type { ExtensionSchema } from '@/vantage/types/Extension.types';
import type { InstanceConfig } from '@/vantage/types/TableConfig.types';
import type { LockedMetadataIF } from '@/workbook/workbook.constants';
import type {
  StoreWorkbook,
  StoreWorkbookWithWorksheets,
  StoreWorksheet,
  VantageWorkbook,
} from '@/worksheet/worksheet.types';
import type { ItemOutputV1 } from '@seeqdev/api/model/ItemOutputV1';
import type { ProjectTypeEnum } from '@seeqdev/api/model/ProjectInputV1';
import type { WorkbookOutputV1 } from '@seeqdev/api/model/WorkbookOutputV1';

export function getTabFolder(folderId: string | undefined) {
  if (!_.isUndefined(folderId) && (folderId !== HOME_SCREEN_TABS.USERS || isAdmin())) {
    if (sqHomeScreenStore.tabFolders[folderId]) {
      return Promise.resolve(sqHomeScreenStore.tabFolders[folderId]) as any;
    } else {
      return sqFoldersApi
        .getFolder({ folderId: getFolderRoot(folderId) as string })
        .then((response: any) => {
          // response.data returns the string "null" when the folder doesn't exist
          if (response.data !== 'null') {
            const name = getFolderName(folderId) as string;
            const folder = _.assign({}, response.data, {
              name: `${i18next.t(name)}`,
            });
            flux.dispatch('SET_TAB_FOLDER', { key: folderId, folder });
            return folder;
          }
        })
        .finally(() => sqHomeScreenStore.tabFolders[folderId]);
    }
  } else {
    return Promise.resolve(undefined);
  }
}

function maybeJson<T>(value: string | undefined, defaultValue: T): T {
  return value === undefined ? defaultValue : JSON.parse(value);
}

function extractWorkbookProperties(workbookWithProperties: ItemOutputV1): {
  extensions: ExtensionSchema[];
  includeExtinct: boolean;
  tableIds?: AnyProperty<string>;
  nameBasedAssetContextOverride: boolean;
  isManualEventsEnabled: boolean;
} {
  const properties = Object.fromEntries(workbookWithProperties.properties.map(({ name, value }) => [name, value]));
  const tableIds: AnyProperty<string> | undefined = properties.tableIds
    ? maybeJson(properties.tableIds, {})
    : undefined;
  return {
    extensions: maybeJson(properties.extensions, []),
    includeExtinct: _.isNil(properties.includeExtinct) || properties.includeExtinct === 'true',
    tableIds,
    nameBasedAssetContextOverride:
      properties.nameBasedAssetContextOverride !== undefined
        ? properties.nameBasedAssetContextOverride === 'true'
        : isVantageNameBasedAssetContextEnabled(),
    isManualEventsEnabled: maybeJson(properties.isManualEventsEnabled, false),
  };
}

export async function getWorkbookConfig(workbookId: string, properties: AnyProperty<any>): Promise<InstanceConfig> {
  const { install, enabled } = setup.getExtensionConfigs(properties.extensions);

  if (install.length > 0) {
    await setup.installExtensions(workbookId, install);
  }

  return {
    workbookId,
    extensions: enabled,
    includeExtinct: properties.includeExtinct,
    tableConfig: setup.tableConfig(),
    tableIds: { evidence: properties.evidenceTableDefinitionId, ...properties?.tableIds },
    useAssetNameForContextLookup: properties.nameBasedAssetContextOverride,
    isManualEventsEnabled: properties.isManualEventsEnabled,
  };
}

export async function initializeVantageWorkbook(
  workbookWithProperties: ItemOutputV1,
  workbook: WorkbookOutputV1,
): Promise<VantageWorkbook> {
  const baseProperties = extractWorkbookProperties(workbookWithProperties);
  const propertiesWithoutId = _.isEmpty(baseProperties.extensions)
    ? await setup.initializeVantage(workbookWithProperties.id)
    : baseProperties;

  const properties = { ...propertiesWithoutId, evidenceTableDefinitionId: workbook.evidenceTableDefinitionId! };

  const workbookConfig = await getWorkbookConfig(workbookWithProperties.id, properties);
  const { data: evidenceTableDefinition } = await sqTableDefinitionsApi.getTableDefinition({
    id: workbook.evidenceTableDefinitionId!,
  });
  return { evidenceTableDefinition, workbookConfig };
}

/**
 * Returns a placeholder folder object representing the "root" workbench level.
 *
 * @returns {Promise} that resolves with an object representing the root folder.
 */
export function getWorkbenchFolderPlaceholder() {
  return {
    name: i18next.t('WORKBENCH.ROOT'),
    id: null,
    workbookId: null,
  };
}

/**
 * Returns the expected icon for the provided item.
 *
 * @param {Object} item - and object representing a workbench explorer item (Analysis, Topic or Folder)
 * @returns {String} representing the icon css class.
 */
export function getItemIcon(item: Item) {
  let iconClass;
  switch (_.get(item, 'type')) {
    case ITEM_TYPES.FOLDER:
      iconClass = 'fa-folder-open';
      break;
    case ITEM_TYPES.PROJECT:
      iconClass = 'fc-seeq-datalab';
      break;
    case ITEM_TYPES.TOPIC:
      iconClass = 'fc-report';
      break;
    case ITEM_TYPES.JOURNAL:
      iconClass = 'fa-book';
      break;
    case ITEM_TYPES.REPORT:
      iconClass = 'fa-book';
      break;
    case ITEM_TYPES.VANTAGE:
      iconClass = 'fc-vantage';
      break;
    case ITEM_TYPES.ACTION_BOARD:
      iconClass = 'fa-sparkles actionBoard workbenchDisplay';
      break;
    case ITEM_TYPES.CONTEXT_COMMENT:
      iconClass = 'fa-comment-dots sq-text-vantage';
      break;
    default:
      iconClass = 'fc-analysis';
  }

  if (!canModifyWorkbook(item, false)) {
    if (_.get(item, 'type') === ITEM_TYPES.FOLDER) {
      iconClass = 'fa-kit fa-sharp-regular-folder-open-lock';
    } else if (_.get(item, 'type') !== ITEM_TYPES.CONTEXT_COMMENT) {
      iconClass += '-lock';
    }
  }
  return iconClass;
}

/**
 * Returns a translated breadcrumb name if translationKey is available.
 *
 * @param {String} crumb - a folder from the breadcrumbs for translating
 * @returns {String} representing a translated/original folder name in the breadcrumb.
 */
export function translateBreadcrumb(
  crumb:
    | {
        translationKey?: string;
        id: string;
        name: string;
      }
    | undefined,
  isMyFolder = false,
  corporateFolderId = '',
) {
  if (!crumb) {
    return;
  }

  if (crumb.translationKey) {
    return i18next.t(`HOME_SCREEN.TABS.${crumb.translationKey}`);
  } else {
    if (crumb.id === corporateFolderId) {
      return i18next.t('HOME_SCREEN.LOCATION.CORPORATE');
    } else if (isMyFolder) {
      return i18next.t('HOME_SCREEN.LOCATION.MY_FOLDER');
    } else {
      return crumb.name;
    }
  }
}

export function translateMeilisearchBreadcrumb(crumb: string | undefined) {
  if (!crumb) {
    return crumb;
  }
  const normalizedCrumb = crumb.replace(/__/g, '').toUpperCase();

  const translatedBreadcrumb = i18next.t(`HOME_SCREEN.TABS.${normalizedCrumb}`, { nsSeparator: false });
  const breadcrumbLabel = translatedBreadcrumb.startsWith('HOME_SCREEN.TABS.') ? crumb : translatedBreadcrumb;

  return breadcrumbLabel;
}

/**
 * Get the collection of workbench items (folders, topics, analyses).
 *
 * @param {String} [filter] - Specify what to be returned: 'owner' for all workbooks owned by the current user,
 *   'shared' for all workbooks shared with the current user or 'all' (or undefined) to return workbooks for all users
 * @param {String} [sortOrder] - Specify the column and sort direction by which to sort the workbooks. Format is the
 *   field name followed by "asc" or "desc". For example, "updatedAt desc"
 * @param {String} [folderId] - Id of the folder
 * @param {Boolean} [isArchived] - True to return only archived items, false to return only unarchived items
 * @param {String} [cancellationGroup] - Id to use for request cancellation
 * @return {Promise} Resolves with the collection of names and ids for the workbooks
 */
export function getWorkbenchItems(
  filter?: FilterEnum,
  sortOrder?: string,
  folderId?: string,
  cancellationGroup?: string,
  isArchived = false,
) {
  // This method is only used by the workstep analyzer...
  const params = { filter, sortOrder, limit: 10000, cancellationGroup };

  if (folderId) {
    _.assign(params, { folderId });
  }
  if (isArchived) {
    _.assign(params, { isArchived });
  }

  return sqFoldersApi.getFolders(params).then((response) => _.map(response.data.content, toWorkbook));
}

/**
 * Helper function that creates a workbook object to be dispatched to a store
 *
 * @param {Object} workbook - The workbook from the API
 * @param {Boolean} isLocked - True if the workbook is locked
 * @param {Object} lockedMetadata - Who locked this workbook and when
 * @returns {Object} Workbook for a store
 */
export function toWorkbook(
  workbook: WorkbookOutputV1,
  isLocked = false,
  lockedMetadata: LockedMetadataIF | undefined = undefined,
): StoreWorkbook {
  return _.assign(workbook, {
    workbookId: workbook.id,
    createdAt: workbook.createdAt!,
    updatedAt: workbook.updatedAt!,
    isLocked,
    lockedMetadata,
  });
}

/**
 * Creates a workbook for the current user with default workbook state initialized.
 *
 * @param {String} name - The workbook name
 * @param {String} data - A stringified object to be stored on the workbook data property
 * @param {String} [branchFrom] - The ID of the workbook from which to branch if wanting to copy an existing
 *   workbook
 * @param {String} [folderId] - Id of the folder
 */
export function createWorkbook(
  name: string,
  branchFrom: string | undefined,
  folderId: string | null | undefined,
  type: string,
) {
  return sqWorkbooksApi
    .createWorkbook({ name, branchFrom, folderId: folderId ?? undefined, type })
    .then((response) => response.data)
    .then((data) => setWorkBook(data.id, DEFAULT_WORKBOOK_STATE).then(() => data))
    .then((workbook) => {
      setWorkbookDisplayMode(WORKBOOK_DISPLAY.EDIT);
      return toWorkbook(workbook);
    });
}

/**
 * Returns the specified folder object. Note that this does not return the contents of the folder.
 *
 * @param {String} folderId - id of the folder to get
 * @returns {Promise<any>} that resolves with the folder object. Also ensures that the folder object is
 *   assigned a workbookId as every item is assumed to have a workbookId. Ideally we'd just refactor this to id or
 *   itemId but that seems a little overwhelming right now.
 */
export function getFolder(folderId: string) {
  return sqFoldersApi.getFolder({ folderId }).then(({ data }) => _.assign(data, { workbookId: data.id }));
}

/** @exportedForTesting */
export async function buildVantageWorksheets(
  workbook: WorkbookOutputV1,
  worksheets: StoreWorksheet[],
  homeWorksheetId: string | undefined,
  pinnedIds: string[],
) {
  const {
    data: { items: worksheetsWithCreators },
  } = await sqItemsApi.searchItems({
    filters: worksheets.map((worksheet) => `ID==${worksheet.worksheetId}`),
    types: [SeeqNames.Types.Worksheet],
    includeProperties: ['creatorId'],
    offset: 0,
    limit: worksheets.length,
  });

  const worksheetIdToCreatorId = new Map(
    worksheetsWithCreators?.flatMap((worksheet) => {
      const creatorId = worksheet?.includedProperties?.creatorId?.value;
      return creatorId ? [[worksheet.id, creatorId]] : [];
    }) ?? [],
  );
  const creatorIdAndNames = await Promise.all(
    _.uniq(Array.from(worksheetIdToCreatorId.values())).map(
      async (creatorId): Promise<[string, string | undefined]> => {
        try {
          const { data } = await sqUsersApi.getUser({ id: creatorId });
          return [creatorId, data.name];
        } catch (error: any) {
          // Don't show error if worksheet's creator has been deleted.
          if (creatorId && !error.data?.statusMessage?.match(/^The item with ID .* could not be found$/)) {
            errorToast({ httpResponseOrError: error });
          }
          return [creatorId, undefined];
        }
      },
    ),
  );
  const creatorIdToCreatorName = new Map(creatorIdAndNames.filter((pair) => pair[1]));

  return worksheets.map((worksheet: StoreWorksheet) => {
    const creatorId = worksheetIdToCreatorId.get(worksheet.worksheetId!);
    return {
      ...worksheet,
      isHome: homeWorksheetId === worksheet.worksheetId,
      isPinned: pinnedIds.includes(worksheet.worksheetId!),
      creatorId: creatorId || workbook.owner?.id,
      creatorName: creatorId ? creatorIdToCreatorName.get(creatorId) : (workbook.owner?.name ?? ''),
    };
  });
}

/**
 * Get the properties of a Workbook and the Workbook's worksheets
 *
 * @param workbookId - The GUID of the workbook to be fetched
 * @param [options] - object container for options
 * @param [options.includeWorkstepId = false] - true to have the returned worksheets include the current workstep ID
 * @param [options.includeArchivedWorksheets = false] - true to return the workbook's archived worksheets
 * @return {promise} resolves with the name and worksheets of the workbook
 */
export async function getWorkbook(
  workbookId: string,
  {
    includeWorkstepId = false,
    includeArchivedWorksheets = false,
  }: {
    includeWorkstepId?: boolean;
    includeArchivedWorksheets?: boolean;
  } = {},
): Promise<StoreWorkbookWithWorksheets> {
  const [
    workbook,
    workbookWithProperties,
    {
      data: { home: homeWorksheetId },
    },
    {
      data: { items: pinned },
    },
  ] = await Promise.all([
    getWorkbookOnly(workbookId),
    sqItemsApi.getItemAndAllProperties({ id: workbookId }),
    sqItemsApi.getItemUserAttributes({ id: workbookId }),
    sqItemsApi.getPinnedItems({ sourceId: undefined }),
  ]);

  const isLocked =
    workbookWithProperties.data.properties.find((property) => property.name === SeeqNames.Properties.Locked) ??
    undefined;
  const lockedMetadata =
    workbookWithProperties.data.properties.find((property) => property.name === SeeqNames.Properties.LockedMetadata) ??
    undefined;
  const pinnedIds = pinned?.map(({ id }) => id) ?? [];

  let worksheets = await getWorksheets(
    workbookId,
    includeWorkstepId,
    (includeArchivedWorksheets || workbook.isArchived) ?? false,
  );

  // Augment worksheets for vantage with additional data, but bypass on creation to avoid unfiltered item searches
  if (workbook.type === ITEM_TYPES.VANTAGE && worksheets.length > 0) {
    worksheets = await buildVantageWorksheets(workbook, worksheets, homeWorksheetId, pinnedIds);
  }

  // Workbooks that were archived prior to R53 didn't have their unarchived worksheets archived along with
  // them. If a workbook existed before R53 and we open it from the trash folder, we need to return its
  // unarchived worksheets instead
  if (_.isEmpty(worksheets) && includeArchivedWorksheets) {
    worksheets = await getWorksheets(workbookId, includeWorkstepId, false);
  }
  // property.data.value comes in as a string, so we convert it to a boolean here
  const parsedLockedMetadata: LockedMetadataIF = lockedMetadata?.value ? JSON.parse(lockedMetadata?.value) : undefined;
  const initializedWorkbook = _.assign(toWorkbook(workbook, isLocked?.value === 'true', parsedLockedMetadata), {
    worksheets,
  });

  return workbook.type === ITEM_TYPES.VANTAGE
    ? { ...initializedWorkbook, ...(await initializeVantageWorkbook(workbookWithProperties.data, workbook)) }
    : initializedWorkbook;
}

/**
 * Gets a Workbook without its worksheets
 *
 * @param {String} workbookId - The GUID of the workbook to be fetched
 * @return {promise} resolves with the workbook
 */
export function getWorkbookOnly(workbookId: string) {
  return sqWorkbooksApi.getWorkbook({ id: workbookId }).then(({ data }) => data);
}

export function getWorkbenchItem(itemId: string) {
  return sqFoldersApi.getFolders({ ids: [itemId] }).then((response) => _.get(response, 'data.content[0]'));
}

/**
 * Creates a new folder.
 *
 * @param name - the name of the folder.
 * @param [parentFolderId] - the id of the parent folder. If no parent is provided the folder is created at
 *   the workbench level.
 * @param [branchFrom] - the id of the folder to duplicate, if desired.
 * @param [ownerId] - the id of the new owner
 * @returns that resolves once the folder has been created and the response was converted to
 *   a proper "workbook" object
 */
export function createFolder(
  name: string,
  parentFolderId: string | null | undefined,
  branchFrom: string | undefined = undefined,
  ownerId: string | undefined = undefined,
) {
  return sqFoldersApi
    .createFolder({ name, parentFolderId: parentFolderId ?? undefined, branchFrom, ownerId })
    .then(({ data }) => data)
    .then(toWorkbook);
}

export function createProject(name: string, folderId: string | null | undefined) {
  return sqProjectsApi
    .createProject({ name, folderId: folderId ?? undefined })
    .then(({ data }) => data)
    .then(toWorkbook);
}

/**
 * Moves an item to the specified folder.
 *
 * @param {String} itemId - id of the item to move.
 * @param {String} destinationFolderId - the id of the folder the item should be moved to.
 * @returns {Promise} that resolves when the item was moved.
 */
export function moveItem(itemId: string, destinationFolderId: string) {
  return sqFoldersApi.moveItemToFolder({
    folderId: destinationFolderId,
    itemId,
  });
}

export function fireMoveItemToast(movedItem: { name: string }, folder: { name: string }) {
  return successToast({
    messageKey: 'WORKBENCH.ITEM_MOVED',
    messageParams: {
      item: movedItem.name,
      folder: folder.name,
    },
  });
}

/**
 * This function is used to move an item to the root level "folder" aka "home".
 *
 * @param {String} assignedFolder - id of the folder the item currently is assigned to.
 * @param {String} itemId - the id of the item to move.
 */
export function removeContentFromFolder(assignedFolder: string, itemId: string) {
  return sqFoldersApi.removeItemFromFolder({
    folderId: assignedFolder,
    itemId,
  });
}

/**
 * Sets a property on a workbook
 *
 * @param {String} property - The name of the property
 * @param {String} workbookId - The ID of the workbook
 * @param {*} value - The value of the property
 * @returns {Promise} - Resolves when the property is set
 */
export function setProperty(property: string, workbookId: string, value: unknown) {
  // Description is an optional property
  if (property === 'Description' && _.isEmpty(value)) {
    return sqItemsApi.deleteProperty({ id: workbookId, propertyName: property }).catch(_.noop);
  } else {
    return sqItemsApi.setProperty({ value }, { id: workbookId, propertyName: property });
  }
}

/**
 * Sets a projectType on a project
 *
 * @param {String} projectId - The ID of the project
 * @param {String} name - The name of the project
 * @param {ProjectTypeEnum} projectType - The projectType to set
 * @returns {Promise} - Resolves when the projectType is set
 */
export function setProjectType(projectId: string, name: string, projectType: ProjectTypeEnum) {
  return sqProjectsApi.putProject({ name, projectType }, { id: projectId });
}

/**
 * Sets the archive property on a workbook.
 *
 * @param {String} workbookId - The ID of the workbook
 * @param {Boolean} isArchived - The value of the property
 * @returns {Promise} - Resolves when the property is set
 */
export function setArchived(workbookId: string, isArchived: boolean) {
  return setProperty(SeeqNames.Properties.Archived, workbookId, isArchived);
}

/**
 * Toggles an item's favorite status.
 *
 * @param {Object} item - representing a workbench explorer item.
 * @param {String} item.workbookId - id of the item.
 * @returns {Promise} - resolves when the favorite status was set.
 */
export function toggleIsPinned(item: { workbookId: string; isPinned?: boolean }) {
  if (item.isPinned) {
    return sqItemsApi.unpinItem({ itemId: item.workbookId });
  } else {
    return sqItemsApi.pinItem({ itemId: item.workbookId });
  }
}

/**
 * Gets the recently accessed list for the workbook.
 *
 * @param {String} workbookId - The ID of the workbook
 * @return {Promise} Resolves with the list of recently accessed items.
 */
export function getRecentlyAccessed(workbookId: string) {
  return sqWorkbooksApi
    .getRecentlyAccessed({ workbookId })
    .then((response) => response.data)
    .then((data) => addAssetsProperty(data));
}

/**
 * Adds an item to the recently accessed list for the workbook.
 *
 * @param {String} workbookId - The ID of the workbook
 * @param {String} id - The ID of the item that was recently accessed
 * @return {Promise} Resolves with the new list of recently accessed items.
 */
export function addRecentlyAccessed(workbookId: string, id: string) {
  return sqWorkbooksApi
    .addRecentlyAccessed({ workbookId, itemId: id })
    .then((response) => response.data)
    .then((data) => addAssetsProperty(data));
}

/**
 * Returns true if item is a folder, otherwise false (for workbooks/topics)
 *
 * @param {Object} item - Object to test
 * @param {String} item.type - Property containing the item type
 * @returns {boolean} True if folder, otherwise false
 */
export function isFolder(item: Item): item is Folder {
  return _.get(item, 'type') === ITEM_TYPES.FOLDER;
}

export function isProject(item: Item) {
  return _.get(item, 'type') === ITEM_TYPES.PROJECT;
}

export function isVantage(item: Item) {
  return _.get(item, 'type') === ITEM_TYPES.VANTAGE;
}

/**
 * Opens a project in a new window.
 * @param {String} projectId - the ID of the project to open
 * @param {MouseEvent | KeyboardEvent} [event] - an optional event used to determine keys that are depressed
 * @returns {Promise} a promise that resolves once the new project window has been opened or once an error
 * notification has been displayed
 */
export function openProject(projectId: string, event?: React.MouseEvent | React.KeyboardEvent): Promise<any> {
  if (hasValidFeatureInLicense(sqLicenseManagementStore.getState().license, Feature.Data_Lab)) {
    const target = sqWorkbenchStore.preferNewTab || event?.ctrlKey || event?.metaKey ? '_blank' : '_self';
    doTrack('Project', 'Opened', `id=${projectId}`);
    return Promise.resolve(window.open(`/data-lab/${projectId.toUpperCase()}`, target)).then(() =>
      updateOpenedAt(projectId),
    );
  } else {
    warnToast({ messageKey: 'WORKBENCH.DATA_LAB_DISABLED' });
    return Promise.resolve();
  }
}

/**
 * Logs the current time the time when the user last accessed the item.
 * @param {String} id - the ID of the item that was opened
 */
export function updateOpenedAt(id: string) {
  if (id) {
    sqItemsApi.setItemUserAttributes({ openedAt: moment().toISOString() }, { id });
  }
}

/**
 * Filters out:
 * 'Corporate' when the Everyone group is disabled
 * admin only options for non-admins
 * 'Recent' if recent is false
 * @param {Object} constToFilter - either of the constants HOME_SCREEN_TABS_AND_TRANSLATION_KEYS or
 *   SEARCH_LOCATION_OPTIONS
 * @param {Boolean} recent - filter out 'recent'
 */
export function getFilteredLocations<T extends { value: string; text: string }>(
  constToFilter: T[],
  recent = true,
): T[] {
  if (_.isArray(constToFilter)) {
    let filteredConst = isAdmin() ? constToFilter : _.reject(constToFilter, { adminOnly: true });

    if (!isExternalOrganizationTabEnabled()) {
      filteredConst = _.reject(
        filteredConst,
        (option) =>
          option.value === SEARCH_ITEM_LOCATIONS.EXTERNALLY_SHARED ||
          option.value === HOME_SCREEN_TABS.EXTERNALLY_SHARED,
      );
    }

    filteredConst = recent
      ? filteredConst
      : _.reject(filteredConst, (option) => _.toUpper(option.value) === HOME_SCREEN_TABS.RECENT);

    return filteredConst;
  }
  return constToFilter;
}

/**
 * Creates a default folder name.
 */
export function getDefaultFolderName() {
  moment.locale(_.truncate(sqWorkbenchStore.userLanguage, { length: 2, omission: '' }));
  const folderTimeFormat = getPreferredTimeFormat(sqWorkbenchStore.use24HourFormat, 'lll');
  return `${i18next.t('ITEM_TYPES.FOLDER')} ${formatTime(new Date(), sqWorksheetStore.timezone, folderTimeFormat)}`;
}

type TabFolderRecord = Record<string, { id?: string } | undefined>;

export function getTabFolderName(id: string | null | undefined) {
  if (_.isNil(id)) {
    return Promise.resolve(undefined);
  }

  let folderPromise;
  const tabFoldersRecord = sqHomeScreenStore.tabFolders as TabFolderRecord;
  const tabFolderKeys = _.keys(tabFoldersRecord);
  const tabKey = _.findKey(tabFoldersRecord, (tabFolder) =>
    tabFolder?.id ? equalsIgnoreCase(tabFolder.id, id) : undefined,
  );

  if (!_.isUndefined(tabKey)) {
    return Promise.resolve(tabKey);
  } else {
    const useTabFolders = tabFolders({
      isAdmin: isAdmin(),
      includeCorporate: isEveryoneGroupEnabled(),
      includeExternallyShared: isExternalOrganizationTabEnabled(),
    });
    if (useTabFolders.length > tabFolderKeys.length) {
      const missingTabFolders = _.difference(useTabFolders, tabFolderKeys);
      folderPromise = Promise.all(_.map(missingTabFolders, (tabFolderName) => getTabFolder(tabFolderName)));
    } else {
      folderPromise = Promise.resolve();
    }

    return folderPromise.then(() => {
      const refreshedTabFoldersRecord = sqHomeScreenStore.tabFolders as TabFolderRecord;
      return _.findKey(refreshedTabFoldersRecord, (tabFolder) =>
        tabFolder?.id ? equalsIgnoreCase(tabFolder.id, id) : undefined,
      );
    });
  }
}

export function getFolderRoot(currentTab: string | null): string | undefined {
  switch (currentTab) {
    case HOME_SCREEN_TABS.USERS:
      return SEARCH_ITEM_LOCATIONS.USERS;
    case HOME_SCREEN_TABS.MY_FOLDER:
      return SEARCH_ITEM_LOCATIONS.MY_FOLDER;
    case HOME_SCREEN_TABS.SHARED:
      return SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC;
    case HOME_SCREEN_TABS.CORPORATE:
      return SEARCH_ITEM_LOCATIONS.CORPORATE;
    case HOME_SCREEN_TABS.EXTERNALLY_SHARED:
      return SEARCH_ITEM_LOCATIONS.EXTERNALLY_SHARED;
    default:
      return undefined;
  }
}

export function getFolderName(currentTab: string) {
  switch (currentTab) {
    case HOME_SCREEN_TABS.USERS:
      return 'HOME_SCREEN.LOCATION.USERS';
    case HOME_SCREEN_TABS.MY_FOLDER:
      return 'HOME_SCREEN.LOCATION.MY_FOLDER';
    case HOME_SCREEN_TABS.SHARED:
      return 'HOME_SCREEN.LOCATION.SHARED_OR_PUBLIC';
    case HOME_SCREEN_TABS.CORPORATE:
      return 'HOME_SCREEN.LOCATION.CORPORATE';
    case HOME_SCREEN_TABS.EXTERNALLY_SHARED:
      return 'HOME_SCREEN.LOCATION.EXTERNALLY_SHARED';
    default:
      return null;
  }
}

export function formatJournalReportContent(searchText: string | undefined, id: string) {
  return sqItemsApi
    .getProperty({
      id,
      propertyName: SeeqNames.Properties.PlainTextDocument,
    })
    .then((results) => {
      let content = results?.data?.value;
      if (_.includes(_.toLower(content), _.toLower(searchText)) && searchText) {
        // if the searchText is found, set setJournalReportContent to the portion of the document content
        // where the searchText is
        content = content?.substring(
          _.toLower(content).indexOf(searchText) - 100,
          _.toLower(content).indexOf(searchText) + 200,
        );
      } else {
        // if the searchText is empty or not found, set setJournalReportContent to a portion of the document
        // content
        content = content?.substring(0, 300);
      }
      return content;
    });
}

/**
 * Sets up a folder from a specific id and returns the folder contents, including a full list of ancestors with
 * the root ancestor, as well. There are some cases in the Shared and User folders where the root ancestors need to be
 * manually added to the folder ancestor list.
 * @param {String} folderId - the nested folder id
 * @param {String} locationValue - the tab where the nested folder is located
 * @param {Boolean} isInAdvancedSearchBreadcrumbClick - checks if the function is being called when inside the
 * home screen advanced search and the user clicks on one of the breadcrumbs
 */
export function formatNestedFolderWithAncestors(
  folderId: string,
  locationValue: string,
  isInAdvancedSearchBreadcrumbClick: boolean,
) {
  const locationUsers = locationValue === SEARCH_ITEM_LOCATIONS.USERS;
  return sqFoldersApi.getFolder({ folderId }).then((response) => {
    return sqFoldersApi.getAncestors({ folderId, root: locationValue }).then((folderTree) => {
      return sqFoldersApi
        .getFolder({
          // This endpoint only has special values of 'corporate', 'mine', and 'users', so when the value
          // would be 'sharedOrPublic' this temporarily gets set to an id that is allowed
          folderId:
            folderTree.data.id === SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC
              ? SEARCH_ITEM_LOCATIONS.USERS
              : folderTree.data.id,
        })
        .then((folderTreeItem) => {
          return sqFoldersApi
            .getFolders({
              filter: FilterEnum.SharedOrPublic,
              types: ['Folder'],
            })
            .then((sharedFolderResponse) => {
              // This manually gets the shared folder to use as the root ancestor when a shared subfolder is
              // missing the root
              const sharedFolder = _.filter(
                sharedFolderResponse.data?.content?.[0]?.ancestors ?? [],
                (ancestor) => ancestor.translationKey === 'SHARED',
              );
              const firstAncestor =
                folderTree.data.id !== SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC ||
                (isInAdvancedSearchBreadcrumbClick && locationValue !== SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC)
                  ? folderTreeItem.data
                  : sharedFolder[0];
              return sqFoldersApi
                .getFolders({
                  filter: FilterEnum.Mine,
                  types: ['Folder'],
                })
                .then((myFolderResponse) => {
                  // When in the user folder and a nested folder is selected that is not in the folder of the
                  // current user, this gets the correct parent user folder to set the second ancestor.
                  // When in the user folder and a nested folder is selected within the current user's folder,
                  // this gets the folder of the current user to set the second ancestor.
                  const userFolderAncestor =
                    locationUsers && (response.data.ancestors?.length ?? 0) === 0
                      ? _.filter(
                          folderTree.data.subfolders,
                          (subfolder) => subfolder.id === response.data.parentFolderId,
                        )
                      : _.filter(
                          myFolderResponse.data.content?.[0]?.ancestors ?? [],
                          (ancestor) => ancestor.translationKey === 'MY_FOLDER',
                        );
                  const secondAncestor =
                    (folderTree.data.id === SEARCH_ITEM_LOCATIONS.MY_FOLDER &&
                      locationUsers &&
                      response.data.id !== userFolderAncestor[0]?.id) ||
                    (locationUsers && (response.data.ancestors?.length ?? 0) === 0)
                      ? userFolderAncestor[0]
                      : undefined;
                  const folderItem = _.assign({}, response.data, {
                    ancestors:
                      secondAncestor && isInAdvancedSearchBreadcrumbClick
                        ? [firstAncestor, secondAncestor, ...(response.data.ancestors ?? [])]
                        : [firstAncestor, ...(response.data.ancestors ?? [])],
                  });
                  return folderItem;
                });
            });
        });
    });
  });
}

/**
 * Resolves the single root homescreen tab for an untrusted user. Trusted users are not limited to one tab; this is only
 * for callers that already know the user is untrusted.
 *
 * When the externally shared (organization) tab is available, the sidebar shows only that tab; otherwise it shows only
 * {@link HOME_SCREEN_TABS.HOME}.
 *
 * @param hideExternallySharedFolder - `true` when there are no external organizations, the feature is off, or the
 *   folder should not be shown; in that case the only root tab is HOME.
 * @returns {@link HOME_SCREEN_TABS.HOME} or {@link HOME_SCREEN_TABS.EXTERNALLY_SHARED}.
 */
export function getHomeScreenTabForUntrustedUsers(hideExternallySharedFolder: boolean): HOME_SCREEN_TABS {
  return hideExternallySharedFolder ? HOME_SCREEN_TABS.HOME : HOME_SCREEN_TABS.EXTERNALLY_SHARED;
}
